import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { todayDubai } from "../../utils/dateUtils";

const STATUSES = [
  { value:"UNDER_OBSERVATION",  label:"Under Observation" },
  { value:"APPROVED",           label:"Approved" },
  { value:"NEED_FURTHER_STUDY", label:"Need Further Study" },
  { value:"REJECTED",           label:"Rejected" },
];

function Row({ label, children }) {
  return (
    <div className="form-row">
      <span className="form-label">{label}</span>
      <div style={{ display:"flex", gap:6, alignItems:"center", flex:1 }}>{children}</div>
    </div>
  );
}

export function RmEvaluationModal({ rms = [], onSave, onCancel }) {
  const [step, setStep] = useState(1);

  // Step 1 — header
  const [evalType,  setEvalType]  = useState("ALTERNATE");
  const [rmId,      setRmId]      = useState("");
  const [rmName,    setRmName]    = useState("");
  const [rmCode,    setRmCode]    = useState("");
  const [date,      setDate]      = useState(todayDubai());
  const [supplier,  setSupplier]  = useState("");
  const [purpose,   setPurpose]   = useState("");
  const [status,    setStatus]    = useState("UNDER_OBSERVATION");
  const [remarks,   setRemarks]   = useState("");

  // Step 2 — params
  const [specParams,  setSpecParams]  = useState([]);
  const [loadingSpec, setLoadingSpec] = useState(false);
  // For ALTERNATE: {paramName: measuredValue}
  const [measured, setMeasured] = useState({});
  // For NEW: [{parameter, measured, uom}]
  const [freeParams, setFreeParams] = useState([{ parameter:"", measured:"", uom:"" }]);

  const [step1Error, setStep1Error] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState(null);

  const sortedRms = [...rms].sort((a, b) => a.id.localeCompare(b.id));

  // When rmId changes for ALTERNATE type, load spec params
  useEffect(() => {
    if (evalType !== "ALTERNATE" || !rmId) {
      setSpecParams([]);
      setMeasured({});
      return;
    }
    setLoadingSpec(true);
    api.fetchRmSpec(rmId)
      .then(res => {
        setSpecParams(res.params ?? []);
        setMeasured({});
      })
      .catch(() => setSpecParams([]))
      .finally(() => setLoadingSpec(false));
  }, [evalType, rmId]);

  function specHint(p) {
    if (p.minValue != null && p.maxValue != null)
      return `${p.minValue} – ${p.maxValue}${p.uom ? " " + p.uom : ""}`;
    if (p.minValue != null) return `≥ ${p.minValue}${p.uom ? " " + p.uom : ""}`;
    if (p.maxValue != null) return `≤ ${p.maxValue}${p.uom ? " " + p.uom : ""}`;
    if (p.expectedValue)    return `Expected: ${p.expectedValue}`;
    return "";
  }

  function goToStep2(e) {
    e.preventDefault();
    setStep1Error(null);
    if (evalType === "ALTERNATE" && !rmId) {
      setStep1Error("Please select a raw material."); return;
    }
    if (evalType === "NEW" && !rmName.trim()) {
      setStep1Error("Product name is required."); return;
    }
    setStep(2);
  }

  function addFreeRow() {
    setFreeParams(p => [...p, { parameter:"", measured:"", uom:"" }]);
  }

  function removeFreeRow(i) {
    setFreeParams(p => p.filter((_, idx) => idx !== i));
  }

  function setFreeField(i, field, value) {
    setFreeParams(p => p.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true); setSaveError(null);
    try {
      let params;
      if (evalType === "ALTERNATE") {
        params = specParams.map(p => ({
          parameter: p.parameter,
          specValue: specHint(p) || null,
          measured:  measured[p.parameter] || null,
          uom:       p.uom || null,
        }));
        if (specParams.length === 0) {
          // No spec defined — single free result
          const measuredVal = measured["Result"];
          if (measuredVal) {
            params = [{ parameter:"Result", specValue:null, measured:measuredVal, uom:null }];
          } else {
            params = [];
          }
        }
      } else {
        params = freeParams
          .filter(p => p.parameter.trim())
          .map(p => ({
            parameter: p.parameter.trim(),
            specValue: null,
            measured:  p.measured.trim() || null,
            uom:       p.uom.trim() || null,
          }));
      }

      await onSave({
        evalType,
        rmId:     evalType === "ALTERNATE" ? rmId     : null,
        rmName:   evalType === "NEW"       ? rmName.trim()   : null,
        rmCode:   evalType === "NEW"       ? rmCode.trim()   : null,
        status,
        date,
        supplier: supplier.trim() || null,
        purpose:  purpose.trim()  || null,
        remarks:  remarks.trim()  || null,
        params,
      });
    } catch (err) {
      setSaveError(err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal wide">
      <div className="modal-header">
        <h3 className="modal-title">New RM Evaluation — Step {step} of 2</h3>
        <button className="btn" onClick={onCancel}>✕</button>
      </div>

      {/* ── Step 1: Header ── */}
      {step === 1 && (
        <form onSubmit={goToStep2}>
          <Row label="Evaluation Type *">
            <label style={{ display:"flex", alignItems:"center", gap:6, marginRight:16 }}>
              <input type="radio" name="evalType" value="ALTERNATE"
                checked={evalType === "ALTERNATE"}
                onChange={() => { setEvalType("ALTERNATE"); setRmId(""); setRmName(""); setRmCode(""); }} />
              Alternate RM
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:6 }}>
              <input type="radio" name="evalType" value="NEW"
                checked={evalType === "NEW"}
                onChange={() => { setEvalType("NEW"); setRmId(""); }} />
              New RM
            </label>
          </Row>

          {evalType === "ALTERNATE" && (
            <Row label="Raw Material *">
              <select className="form-select" value={rmId}
                onChange={e => setRmId(e.target.value)} style={{ flex:1 }}>
                <option value="">— select raw material —</option>
                {sortedRms.map(r => (
                  <option key={r.id} value={r.id}>{r.id} — {r.name}</option>
                ))}
              </select>
            </Row>
          )}

          {evalType === "NEW" && (
            <>
              <Row label="Product Name *">
                <input className="form-input" placeholder="e.g. Vinyl Acetate Monomer"
                  value={rmName} onChange={e => setRmName(e.target.value)} style={{ flex:1 }} />
              </Row>
              <Row label="Product Code">
                <input className="form-input" placeholder="e.g. RM-999"
                  value={rmCode} onChange={e => setRmCode(e.target.value)} style={{ flex:1 }} />
              </Row>
            </>
          )}

          <Row label="Evaluation Date *">
            <input type="date" className="form-input" value={date}
              onChange={e => setDate(e.target.value)} required style={{ width:160 }} />
          </Row>

          <Row label="Supplier">
            <input className="form-input" placeholder="Supplier / vendor name"
              value={supplier} onChange={e => setSupplier(e.target.value)} style={{ flex:1 }} />
          </Row>

          <Row label="Purpose">
            <input className="form-input" placeholder="Reason for evaluation"
              value={purpose} onChange={e => setPurpose(e.target.value)} style={{ flex:1 }} />
          </Row>

          <Row label="Initial Status">
            <select className="form-select" value={status}
              onChange={e => setStatus(e.target.value)} style={{ width:220 }}>
              {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Row>

          <Row label="Remarks">
            <textarea className="form-input" rows={2} placeholder="Optional notes…"
              value={remarks} onChange={e => setRemarks(e.target.value)}
              style={{ flex:1, resize:"vertical" }} />
          </Row>

          {step1Error && <div className="alert-warn" style={{ marginTop:8 }}>{step1Error}</div>}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
            <button type="button" className="btn" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary">Next: Parameters →</button>
          </div>
        </form>
      )}

      {/* ── Step 2: Parameters ── */}
      {step === 2 && (
        <form onSubmit={handleSubmit}>
          <div style={{ fontSize:11, fontWeight:700, color:"#888", marginBottom:8,
            textTransform:"uppercase", letterSpacing:".05em" }}>
            Test Parameters
          </div>

          {evalType === "ALTERNATE" && (
            <>
              {loadingSpec && (
                <div style={{ color:"#aaa", fontSize:12, padding:"8px 0" }}>Loading spec…</div>
              )}
              {!loadingSpec && specParams.length === 0 && (
                <>
                  <div className="alert-warn" style={{ margin:"0 0 8px" }}>
                    No spec defined for this RM — enter result manually.
                  </div>
                  <div className="form-row">
                    <span className="form-label">Result</span>
                    <input className="form-input" placeholder="Enter result…"
                      value={measured["Result"] ?? ""}
                      onChange={e => setMeasured(m => ({ ...m, Result: e.target.value }))}
                      style={{ flex:1 }} />
                  </div>
                </>
              )}
              {!loadingSpec && specParams.map(p => (
                <div key={p.parameter} className="form-row">
                  <span className="form-label">{p.parameter}</span>
                  <div style={{ display:"flex", gap:6, alignItems:"center", flex:1 }}>
                    <input className="form-input" value={measured[p.parameter] ?? ""}
                      onChange={e => setMeasured(m => ({ ...m, [p.parameter]: e.target.value }))}
                      placeholder={specHint(p) || "measured value"}
                      style={{ width:160 }} />
                    {specHint(p) && (
                      <span style={{ fontSize:11, color:"#aaa" }}>spec: {specHint(p)}</span>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {evalType === "NEW" && (
            <>
              {freeParams.map((row, i) => (
                <div key={i} style={{ display:"flex", gap:6, marginBottom:6, alignItems:"center" }}>
                  <input className="form-input" placeholder="Parameter name"
                    value={row.parameter}
                    onChange={e => setFreeField(i, "parameter", e.target.value)}
                    style={{ flex:2 }} />
                  <input className="form-input" placeholder="Measured value"
                    value={row.measured}
                    onChange={e => setFreeField(i, "measured", e.target.value)}
                    style={{ flex:2 }} />
                  <input className="form-input" placeholder="UoM"
                    value={row.uom}
                    onChange={e => setFreeField(i, "uom", e.target.value)}
                    style={{ flex:1 }} />
                  {freeParams.length > 1 && (
                    <button type="button" className="btn-small btn-danger"
                      onClick={() => removeFreeRow(i)}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn" style={{ marginTop:4, fontSize:12 }}
                onClick={addFreeRow}>+ Add Parameter</button>
            </>
          )}

          {saveError && <div className="alert-warn" style={{ marginTop:8 }}>{saveError}</div>}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
            <button type="button" className="btn" onClick={() => setStep(1)} disabled={saving}>← Back</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Evaluation"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
